import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  Menu,
  X,
  Phone,
  Stethoscope,
  HeartHandshake,
  Activity,
  Baby,
  Dumbbell,
  HeartPulse,
  BriefcaseMedical,
  TestTubes,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES } from "../mock";

const SVC_ICONS = {
  Stethoscope,
  HeartHandshake,
  Activity,
  Baby,
  Dumbbell,
  HeartPulse,
  BriefcaseMedical,
  TestTubes,
  Users,
};

const WhatsAppIcon = ({ size = 16, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.945c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.454h.006c6.585 0 11.946-5.36 11.949-11.945a11.821 11.821 0 00-3.481-8.418" />
  </svg>
);

const Logo = () => (
  <a href="#home" className="flex flex-col items-start leading-none select-none">
    <img
      src="/brand/logo.png"
      alt="Healthsqur"
      className="h-[55px] md:h-[60px] w-auto object-contain"
    />
    <span className="text-[8px] tracking-[0.30em] uppercase text-[#1B2A4A] font-semibold mt-1 ml-1">
      you matter to us
    </span>
  </a>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F2F5F8]/90 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.05)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-8 h-[86px] flex items-center justify-between">
        <Logo />

        <div className="hidden xl:flex items-center gap-6">
          <Link to="/" className="text-[14px] font-medium text-[#159A90] whitespace-nowrap">
            Home
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button className="flex items-center gap-1 text-[14px] font-medium text-[#1B2A4A] hover:text-[#159A90] transition-colors whitespace-nowrap">
              Services <ChevronDown size={16} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50">
                <div className="w-[520px] bg-white rounded-2xl shadow-[0_18px_50px_rgba(0,0,0,0.15)] border border-[#DEE6EE] p-4 grid grid-cols-2 gap-1">
                  {SERVICES.map((s) => {
                    const SIcon = SVC_ICONS[s.icon] || Stethoscope;
                    return (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        onClick={() => setServicesOpen(false)}
                        className="group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#E7EDF3] transition-colors"
                      >
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#159A90]/10 shrink-0 group-hover:bg-[#159A90]/20 transition-colors">
                          <SIcon size={18} className="text-[#159A90]" strokeWidth={1.8} />
                        </span>
                        <span className="text-[14px] font-medium text-[#1B2A4A] group-hover:text-[#159A90] transition-colors">
                          {s.title}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <a href="/#why" className="text-[14px] font-medium text-[#1B2A4A] hover:text-[#159A90] transition-colors whitespace-nowrap">
            About
          </a>
          <Link to="/partner" className="text-[14px] font-medium text-[#1B2A4A] hover:text-[#159A90] transition-colors whitespace-nowrap">
            Partner With Us
          </Link>
          <Link to="/careers" className="text-[14px] font-medium text-[#1B2A4A] hover:text-[#159A90] transition-colors whitespace-nowrap">
            Careers
          </Link>
          <a href="/#book" className="text-[14px] font-medium text-[#1B2A4A] hover:text-[#159A90] transition-colors whitespace-nowrap">
            Contact
          </a>
        </div>

        <div className="hidden xl:flex items-center gap-2.5">
          <a
            href="tel:+919611761212"
            className="flex items-center justify-center gap-2 min-w-[150px] px-4 py-2.5 rounded-full bg-[#1B2A4A] text-white text-[13px] font-semibold whitespace-nowrap hover:bg-[#142038] transition-colors shadow-[0_6px_18px_rgba(27,42,74,0.4)]"
          >
            <Phone size={16} className="text-white" /> +91 96117 61212
          </a>
          <a
            href="https://wa.me/919611761212?text=Hi%20Healthsqur%2C%20I'd%20like%20to%20book%20a%20caregiver."
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 min-w-[150px] px-4 py-2.5 rounded-full bg-[#1B2A4A] text-white text-[13px] font-semibold whitespace-nowrap hover:bg-[#142038] transition-colors shadow-[0_6px_18px_rgba(27,42,74,0.4)]"
          >
            <WhatsAppIcon size={16} className="text-white" /> Connect on WhatsApp
          </a>
          <a
            href="/#book"
            className="flex items-center justify-center gap-2 min-w-[150px] px-4 py-2.5 rounded-full bg-[#1B2A4A] text-white text-[13px] font-semibold whitespace-nowrap hover:bg-[#142038] transition-colors shadow-[0_6px_18px_rgba(27,42,74,0.4)]"
          >
            Book a Caregiver
          </a>
        </div>

        <button
          className="xl:hidden text-[#1B2A4A]"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="xl:hidden bg-[#F2F5F8] border-t border-[#DEE6EE] px-5 py-4 flex flex-col gap-3">
          <Link to="/" onClick={() => setMobileOpen(false)} className="py-1 font-medium text-[#159A90]">Home</Link>
          <a href="/#services" onClick={() => setMobileOpen(false)} className="py-1 font-medium text-[#1B2A4A]">Services</a>
          <a href="/#why" onClick={() => setMobileOpen(false)} className="py-1 font-medium text-[#1B2A4A]">About</a>
          <Link to="/partner" onClick={() => setMobileOpen(false)} className="py-1 font-medium text-[#1B2A4A]">Partner With Us</Link>
          <Link to="/careers" onClick={() => setMobileOpen(false)} className="py-1 font-medium text-[#1B2A4A]">Careers</Link>
          <a href="/#book" onClick={() => setMobileOpen(false)} className="py-1 font-medium text-[#1B2A4A]">Contact</a>
          <a
            href="tel:+919611761212"
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center gap-2 mt-2 px-4 py-2.5 rounded-full bg-[#1B2A4A] text-white font-semibold"
          >
            <Phone size={16} className="text-white" /> Call Us · +91 96117 61212
          </a>
          <a
            href="https://wa.me/919611761212?text=Hi%20Healthsqur%2C%20I'd%20like%20to%20book%20a%20caregiver."
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#1B2A4A] text-white font-semibold"
          >
            <WhatsAppIcon size={16} className="text-white" /> Connect on WhatsApp
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
